import styled from 'styled-components'
import { vimovaTheme } from '../../theme/vimovaTheme'

export const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-top: 16px;

  @media print {
    display: grid !important;
    grid-template-columns: 1fr 1fr !important;
    gap: 16px !important;
    margin-top: 16px !important;
  }
`

export const ServiceCard = styled.div`
  background: ${vimovaTheme.colors.white};
  border: 1px solid ${vimovaTheme.colors.border};
  border-radius: ${vimovaTheme.borderRadius.signature};
  padding: 16px 14px;

  @media print {
    background: ${vimovaTheme.colors.white} !important;
    border: 1px solid ${vimovaTheme.colors.border} !important;
    border-radius: ${vimovaTheme.borderRadius.signature} !important;
    padding: 16px 14px !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  @media print {
    display: flex !important;
    align-items: center !important;
    gap: 12px !important;
  }
`

export const IconCircle = styled.div<{ $bgColor: string; $opacity?: number }>`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${({ $bgColor, $opacity }) =>
    $opacity
      ? `${$bgColor}${Math.round($opacity * 255)
          .toString(16)
          .padStart(2, '0')}`
      : $bgColor};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  @media print {
    width: 36px !important;
    height: 36px !important;
    border-radius: 50% !important;
    background: ${({ $bgColor, $opacity }) =>
      $opacity
        ? `${$bgColor}${Math.round($opacity * 255)
            .toString(16)
            .padStart(2, '0')}`
        : $bgColor} !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const IconLetter = styled.span<{ $color: string }>`
  font-family: ${vimovaTheme.fonts.heading};
  font-weight: ${vimovaTheme.fontWeights.bold};
  font-size: ${vimovaTheme.fontSizes.medium};
  color: ${({ $color }) => $color};

  @media print {
    font-weight: ${vimovaTheme.fontWeights.bold} !important;
    font-size: ${vimovaTheme.fontSizes.medium} !important;
    color: ${({ $color }) => $color} !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const HeaderText = styled.div`
  display: flex;
  flex-direction: column;

  @media print {
    display: flex !important;
    flex-direction: column !important;
  }
`

export const CategoryName = styled.div`
  font-family: ${vimovaTheme.fonts.heading};
  font-weight: ${vimovaTheme.fontWeights.bold};
  font-size: 12px;
  color: ${vimovaTheme.colors.primary};

  @media print {
    font-weight: ${vimovaTheme.fontWeights.bold} !important;
    font-size: 12px !important;
    color: ${vimovaTheme.colors.primary} !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const WalkingTime = styled.div`
  font-family: ${vimovaTheme.fonts.body};
  font-weight: ${vimovaTheme.fontWeights.bold};
  font-size: 10px;
  color: ${vimovaTheme.colors.greenDark};
  margin-top: 2px;

  @media print {
    font-weight: ${vimovaTheme.fontWeights.bold} !important;
    font-size: 10px !important;
    color: ${vimovaTheme.colors.greenDark} !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const CardDivider = styled.div`
  height: 1px;
  background: ${vimovaTheme.colors.border};
  margin: 12px 0;

  @media print {
    height: 1px !important;
    background: ${vimovaTheme.colors.border} !important;
    margin: 12px 0 !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

/* ── Main items (first 2) ─────────────────────────────── */

export const AmenityItemRow = styled.div`
  display: flex;
  align-items: baseline;
  line-height: 1.8;
  font-family: ${vimovaTheme.fonts.body};

  @media print {
    display: flex !important;
    align-items: baseline !important;
  }
`

export const AmenityItemName = styled.span`
  font-size: 10px;
  color: ${vimovaTheme.colors.text};

  @media print {
    font-size: 10px !important;
    color: ${vimovaTheme.colors.text} !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const AmenityItemSeparator = styled.span`
  font-size: 10px;
  color: ${vimovaTheme.colors.textMuted};
  margin: 0 3px;

  @media print {
    font-size: 10px !important;
    color: ${vimovaTheme.colors.borderLight} !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const AmenityItemType = styled.span`
  font-size: 9px;
  font-style: italic;
  color: ${vimovaTheme.colors.textMuted};

  @media print {
    font-size: 9px !important;
    font-style: italic !important;
    color: ${vimovaTheme.colors.textMuted} !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const AmenityItemDistance = styled.span`
  margin-left: auto;
  font-size: 9px;
  color: ${vimovaTheme.colors.textMuted};

  @media print {
    margin-left: auto !important;
    font-size: 9px !important;
    color: ${vimovaTheme.colors.textMuted} !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

/* ── Detail items (3-4) ──────────────────────────────── */

export const AmenityDetailRow = styled.div`
  display: flex;
  align-items: baseline;
  line-height: 1.8;
  font-family: ${vimovaTheme.fonts.body};

  @media print {
    display: flex !important;
    align-items: baseline !important;
  }
`

export const AmenityDetailName = styled.span`
  font-size: 8px;
  color: ${vimovaTheme.colors.textLight};

  @media print {
    font-size: 8px !important;
    color: ${vimovaTheme.colors.textLight} !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const AmenityDetailSeparator = styled.span`
  font-size: 8px;
  color: ${vimovaTheme.colors.textMuted};
  margin: 0 3px;

  @media print {
    font-size: 8px !important;
    color: ${vimovaTheme.colors.borderLight} !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const AmenityDetailType = styled.span`
  font-size: 7.5px;
  font-style: italic;
  color: ${vimovaTheme.colors.textMuted};

  @media print {
    font-size: 7.5px !important;
    font-style: italic !important;
    color: ${vimovaTheme.colors.borderLight} !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const AmenityDetailDistance = styled.span`
  margin-left: auto;
  font-size: 8px;
  color: ${vimovaTheme.colors.textMuted};

  @media print {
    margin-left: auto !important;
    font-size: 8px !important;
    color: ${vimovaTheme.colors.borderLight} !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`
